
import {defineComponent} from "vue";
import {FormMixin} from "@/components/base/FormMixin";
import GenericFormWrapper from "@/components/base/GenericFormWrapper.vue";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import AlertWarning from "@/components/UI/Bootstrap/Alert/AlertWarning.vue";
import {Exception} from "@/api/interfaces";
import InputSelect from "@/components/UI/InputSelect.vue";
import InputCheckboxMultiSelect from "@/components/UI/InputCheckboxMultiSelect.vue";
import {CodeDescriptionRestDtoModel} from "@/models/CodeDescriptionRestDtoModel";
import {CustomerOverviewRestDto} from "@/api/pharma-customer/models";
import InputText from "@/components/UI/InputText.vue";
import {handleSavedSuccessfully} from "@/helpers/toast-helper";
import {mapActions as mapActionsP, mapState} from "pinia";
import {useCustomerStore} from "@/stores/CustomerStore";
import {CustomerCriteriaRestDtoModel} from "@/models/api/pharma-customer/CustomerCriteriaRestDtoModel";
import {ContentPageRestService} from "@/services/rest/cms-content-page/ContentPageRestService";
import {SnippetRestService} from "@/services/rest/cms-snippet/SnippetRestService";
import {UIStateDto} from "@/dtos/UIStateDto";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptions} from "@/error/DpExceptions";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import {AuthContext} from "@/context/AuthContext";
import {CustomerContext} from "@/context/CustomerContext";

/**
 * The backend marks a refusal the caller may confirm with is_ignorable: true. The flag is only present
 * when it is true, and the generated Exception interface does not know it.
 */
type IgnorableException = Exception & { is_ignorable?: boolean };

export default defineComponent({
  name: "ContentPageCopy",
  mixins: [FormMixin],
  components: {GenericFormWrapper, InputText, InputSelect, InputCheckboxMultiSelect, AlertError2, AlertWarning},
  props: {
    pageId: {
      type: String,
      required: false
    },
    /**
     * Set for a named page (incl. the homepage): the page is then copied by its type instead of by its id.
     */
    pageTypeCode: {
      type: String,
      required: false
    },
    backRoute: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      searchPageUI: UIStateDto.createWithDefaults(),

      customerCodeCopyTo: null as string | null,
      ignorableExceptions: [] as IgnorableException[],
      ignoreAppErrorCodes: [] as string[],
    }
  },
  computed: {
    ...mapState(useCustomerStore, ["getCriteria", "getCustomers", "getCustomersWithCriteria"]),
    customerOptions(): CodeDescriptionRestDtoModel[] {
      const customerOverview = this.selectableCustomers;
      const customerOverviewCodeDescription = [] as CodeDescriptionRestDtoModel[];

      customerOverview.forEach((customer: CustomerOverviewRestDto) => {
        if (customer?.customer_code) {
          customerOverviewCodeDescription.push(
            new CodeDescriptionRestDtoModel(customer.customer_code, customer.name)
          );
        }
      });

      return customerOverviewCodeDescription;
    },
    customerCriteria(): CustomerCriteriaRestDtoModel {
      return this.getCriteria.customer;
    },
    /**
     * A DP user searches the full customer list. Any other user picks from the customers they already
     * have access to, minus the current one; the backend refuses a target outside their i18n group.
     * A named page can never be copied to the current customer, so it is left out for a DP user too.
     */
    selectableCustomers(): CustomerOverviewRestDto[] {
      if (AuthContext.isDpUser()) {
        const customers = this.getCustomersWithCriteria as CustomerOverviewRestDto[];
        if (!this.pageTypeCode) {
          return customers;
        }

        return customers.filter((customer: CustomerOverviewRestDto) => customer.customer_code !== CustomerContext.getCustomerCode());
      }

      return (this.getCustomers as CustomerOverviewRestDto[])
        .filter((customer: CustomerOverviewRestDto) => customer.customer_code !== CustomerContext.getCustomerCode());
    }
  },
  watch: {
    customerCodeCopyTo(): void {
      this.pageUI.clearError();
      this.clearIgnorableExceptions();
    }
  },
  mounted() {
    this.pageUI.setReady();
    this.searchPageUI.setReady();
  },
  methods: {
    ...mapActionsP(useCustomerStore, ["searchCustomersWithCriteria"]),
    async doSubmit(): Promise<void> {
      if (this.backRoute === 'snippets') {
        await SnippetRestService.getInstance().copySnippetToOtherCustomer(this.$route.params.snippetId, this.customerCodeCopyTo);
      } else if (this.pageTypeCode) {
        await ContentPageRestService.getInstance()
          .copyNamedPageToOtherCustomer(this.pageTypeCode, this.customerCodeCopyTo as string, this.ignoreAppErrorCodes);
      } else {
        await ContentPageRestService.getInstance().copyContentPageToOtherCustomer(this.pageId, this.customerCodeCopyTo);
      }
    },
    async afterSave(): Promise<void> {
      this.clearIgnorableExceptions();
      await handleSavedSuccessfully(this.$t('copiedToCustomer', {
        customer: this.customerOptions
          .find((customer: CodeDescriptionRestDtoModel) => customer.code === this.customerCodeCopyTo)?.description
      }));
    },
    async searchCustomers(): Promise<void> {
      await this.performGenericReload(this.searchCustomersWithCriteria, this.searchPageUI as UIStateDto);
    },
    /**
     * Overrides FormMixin.setExceptions: a refusal that consists of confirmable errors only is not shown as an
     * error, but as a list of checkboxes; the user ticks the ones to ignore and submits again.
     * The codes confirmed before stay in ignoreAppErrorCodes: the backend no longer returns them, but it needs them
     * again on the next submit. A failing customer search (searchPageUI) leaves the list alone.
     */
    setExceptions(exceptions: unknown, pageUI?: UIStateDto): void {
      const pageUIToUse: UIStateDto = pageUI ?? (this.pageUI as UIStateDto);

      if (pageUIToUse === this.pageUI) {
        this.ignorableExceptions = [];
      }
      ErrorHandlerQueue
        .create()
        .add(new DpExceptionsErrorHandler((error: DpExceptions) => this.handleDpExceptions(error, pageUIToUse)))
        .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(pageUIToUse))
        .catch(exceptions, true);
    },
    handleDpExceptions(error: DpExceptions, pageUI: UIStateDto): void {
      const exceptions = error.exceptions as IgnorableException[];
      const isConfirmable = exceptions.length > 0
        && exceptions.every((exception: IgnorableException) => exception.is_ignorable === true);

      if (isConfirmable) {
        this.ignorableExceptions = exceptions;
        return;
      }

      pageUI.setError(DpExceptionsErrorHandler.getHtmlMessageFromError(error));
    },
    clearIgnorableExceptions(): void {
      this.ignorableExceptions = [];
      this.ignoreAppErrorCodes = [];
    },
  }
});
